import { BOB_PHYSICS } from '../../../packages/domain/src/68-bob-bounce.mjs';
import { BOB_ARCADE } from '../../../packages/domain/src/91-bob-arcade.mjs';

/** Finite, disposable feedback from actual domain contacts and reward events.
 * Owns only transient GPU resources. The controller supplies visible seconds;
 * reduced motion keeps exact score text and causal links without flying pieces. */
export function createBobRewardEffects({ THREE, canvas, scene, factory, prepare }) {
  let effects = [];
  const ownedEffects = new Set();
  function disposeEffect(effect) {
    scene.remove(effect.group);
    for (const resource of effect.resources) resource.dispose();
    ownedEffects.delete(effect);
  }
  function clear() {
    for (const effect of ownedEffects) disposeEffect(effect);
    effects = [];
  }
  function makeAward(points) {
    const node = canvas.ownerDocument?.createElement('canvas');
    const ctx = node?.getContext?.('2d');
    if (!ctx) return null;
    node.width = 256;
    node.height = 128;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '900 64px system-ui, sans-serif';
    ctx.lineWidth = 10;
    ctx.strokeStyle = '#59336c';
    ctx.strokeText(`+${points}`, 128, 65);
    ctx.fillStyle = '#fff6a2';
    ctx.fillText(`+${points}`, 128, 65);
    const texture = new THREE.CanvasTexture(node);
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(2.4, 1.2, 1);
    return { sprite, resources: [texture, material] };
  }
  function burst(event, reduced) {
    if (
      ![
        'bounce',
        'food',
        'land',
        'complete',
        'platform',
        'bumper',
        'timing',
        'powerup',
        'speed',
        'checkpoint',
        'shield',
        'stumble',
        'crate',
        'spring',
        'bonus-route',
        'arcade',
        'chain-reaction',
      ].includes(event.type)
    )
      return;
    if (event.type === 'speed') return;
    const group = new THREE.Group();
    group.name = `Bob effect: ${event.type}`;
    const normal = event.normal || { x: 0, y: 1, z: 0 };
    const contact = ['bounce', 'land', 'platform', 'bumper'].includes(event.type);
    const inset = contact ? BOB_PHYSICS.radius : 0;
    group.position.set(
      event.x - normal.x * inset,
      event.y - normal.y * inset,
      event.z - normal.z * inset,
    );
    const colour =
      event.type === 'arcade'
        ? '#ff73d7'
        : event.type === 'chain-reaction'
          ? '#73ffea'
          : event.kind === 'shield' || event.type === 'shield'
            ? '#83d6ff'
            : event.lostGoodies > 0 ||
                ['food', 'complete', 'timing', 'powerup', 'speed', 'checkpoint'].includes(
                  event.type,
                )
              ? '#ffe777'
              : '#73ffea';
    const ring = factory.torus(group, colour, [0, 0, 0], 0.65, 0.7);
    // A direct tap is a compact body-centred sparkle, never a horizontal
    // paddle-like disc beneath Bob. The matching launch event adds no duplicate.
    if (event.type === 'timing') {
      group.name = 'Bob direct tap sparkle';
      ring.visible = false;
    }
    ring.quaternion.setFromUnitVectors(
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(normal.x, normal.y, normal.z).normalize(),
    );
    ring.material = prepare(ring.material.clone());
    ring.material.transparent = true;
    const resources = [ring.material];
    const fragments = [];
    let reaction = null;
    if (event.type === 'chain-reaction' && event.links?.length) {
      const points = event.links
        .slice(0, BOB_ARCADE.reactionLimit)
        .flatMap(({ from, to }) =>
          [from, to].flatMap((p) => [
            p.x - group.position.x,
            p.y - group.position.y,
            p.z - group.position.z,
          ]),
        );
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
      const material = new THREE.LineBasicMaterial({
        color: colour,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
      });
      reaction = new THREE.LineSegments(geometry, material);
      reaction.name = 'Actual collected goodie chain';
      group.add(reaction);
      resources.push(geometry, material);
    }
    if (event.type === 'crate' && !reduced) {
      const geometry = new THREE.BoxGeometry(0.18, 0.26, 0.12);
      const material = prepare(
        new THREE.MeshStandardMaterial({ color: '#d79a54', roughness: 0.8 }),
      );
      resources.push(geometry, material);
      for (let i = 0; i < 6; i++) {
        const piece = new THREE.Mesh(geometry, material);
        group.add(piece);
        fragments.push(piece);
      }
    }
    const count = reduced ? 0 : event.type === 'complete' ? 32 : 12;
    const positions = new Float32Array(count * 3);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const material = new THREE.PointsMaterial({
      color: colour,
      size: 0.13,
      transparent: true,
      depthWrite: false,
    });
    const sparks = new THREE.Points(geometry, material);
    group.add(sparks);
    resources.push(geometry, material);
    const award = event.points > 0 ? makeAward(event.points) : null;
    if (award) {
      group.add(award.sprite);
      resources.push(...award.resources);
    }
    scene.add(group);
    const effect = {
      group,
      ring,
      sparks,
      award,
      resources,
      age: 0,
      life: reduced ? 0.25 : event.type === 'timing' ? 0.42 : 0.85,
      count,
      fragments,
      reaction,
    };
    ownedEffects.add(effect);
    effects.push(effect);
    while (effects.length > 10) disposeEffect(effects.shift());
  }
  function animateEffects(dt, reduced) {
    for (const effect of effects) {
      effect.age += dt;
      const t = Math.min(1, effect.age / effect.life);
      effect.ring.scale.setScalar(reduced ? 0.85 : 0.5 + t * 2.8);
      effect.ring.material.opacity = (1 - t) * 0.8;
      if (effect.reaction) effect.reaction.material.opacity = Math.min(0.9, (1 - t) * 2);
      effect.sparks.visible = !reduced;
      for (let i = 0; i < effect.fragments.length; i++) {
        const piece = effect.fragments[i],
          a = (i * Math.PI) / 3;
        piece.visible = !reduced;
        piece.position.set(Math.sin(a) * t * 1.8, 0.4 + 2 * t - 3 * t * t, Math.cos(a) * t * 1.8);
        piece.rotation.set(t * 5, i + t * 4, t * 3);
        piece.scale.setScalar(1 - t);
      }
      const positions = effect.sparks.geometry.attributes.position;
      for (let i = 0; i < effect.count; i++) {
        const a = i * 2.39996;
        positions.setXYZ(
          i,
          Math.sin(a) * t * 2,
          Math.cos(a * 3) * t + 2 * t - 2 * t * t,
          Math.cos(a) * t * 2,
        );
      }
      positions.needsUpdate = true;
      effect.sparks.material.opacity = 1 - t;
      if (effect.award) {
        effect.award.sprite.position.y = reduced ? 1 : 0.8 + t * 1.6;
        effect.award.sprite.material.opacity = Math.min(1, (1 - t) * 3);
      }
      if (t >= 1) disposeEffect(effect);
    }
    effects = effects.filter((effect) => ownedEffects.has(effect));
  }
  return { burst, update: animateEffects, clear };
}
